/* ---------------------------------------------------------------------------
   Product card videos

   Plays the <video> that snippets/product-card-video.liquid renders in place of
   a card's featured image.

   Why this is not just the `autoplay` attribute:

   1. A collection page renders 24+ cards. `autoplay` on all of them makes the
      browser start fetching every video as soon as the markup parses, which
      competes with the images and the theme's own scripts. Here the source is
      only attached (preload="none" -> load()) when the card is actually in view.

   2. A card with colour swatches renders one wrapper per colour and shows the
      selected one with `.is-active`; the others are `display: none`. Those
      hidden videos must not download or play. IntersectionObserver reports a
      display:none element as not intersecting, and it re-runs when the swatch
      script flips `.is-active`, so both cases fall out of the same observer.

   3. Collection filtering and pagination replace the grid markup. A
      MutationObserver picks up the cards that arrive that way.

   Reduced motion is respected: the poster frame stays, nothing plays.
   --------------------------------------------------------------------------- */
(function () {
	"use strict";

	var SELECTOR = ".js-product-card-autoplay-video";
	var reduceMotion =
		window.matchMedia &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	if (reduceMotion || !("IntersectionObserver" in window)) {
		return;
	}

	function play(video) {
		// The attribute alone is not always enough: Safari checks the property
		// when deciding whether an inline autoplay is allowed.
		video.muted = true;

		if (video.preload === "none") {
			video.preload = "auto";
			video.load();
		}

		var attempt = video.play();

		// Autoplay can still be refused (low power mode, for one). Nothing to do
		// about it — the poster frame stays up, which is the same as before.
		if (attempt && typeof attempt.catch === "function") {
			attempt.catch(function () {});
		}
	}

	var observer = new IntersectionObserver(
		function (entries) {
			entries.forEach(function (entry) {
				if (entry.isIntersecting) {
					play(entry.target);
				} else if (!entry.target.paused) {
					entry.target.pause();
				}
			});
		},
		{ rootMargin: "200px 0px" }
	);

	function observeAll(root) {
		var videos = root.querySelectorAll ? root.querySelectorAll(SELECTOR) : [];

		Array.prototype.forEach.call(videos, function (video) {
			if (video.dataset.cardVideoObserved) {
				return;
			}

			video.dataset.cardVideoObserved = "true";
			observer.observe(video);
		});
	}

	function init() {
		observeAll(document);

		if (!("MutationObserver" in window)) {
			return;
		}

		new MutationObserver(function (mutations) {
			mutations.forEach(function (mutation) {
				Array.prototype.forEach.call(mutation.addedNodes, function (node) {
					if (node.nodeType !== 1) {
						return;
					}

					if (node.matches && node.matches(SELECTOR)) {
						observeAll(node.parentNode || document);
					} else {
						observeAll(node);
					}
				});
			});
		}).observe(document.body, { childList: true, subtree: true });
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
